import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabase';

const COMPLETED_KEY = 'onboarding_flow_completed';

// ── Helpers ───────────────────────────────────────────────────────────────────

async function markFlowCompleted(userId) {
  if (!userId) return;
  await supabase
    .from('onboarding_progress')
    .upsert({ user_id: userId, step_key: COMPLETED_KEY }, { onConflict: 'user_id,step_key' });
}

async function hasCompletedFlow(userId) {
  if (!userId) return false;
  const { data } = await supabase
    .from('onboarding_progress')
    .select('step_key')
    .eq('user_id', userId)
    .eq('step_key', COMPLETED_KEY)
    .maybeSingle();
  return !!data;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ProgressBar({ step, total }) {
  const pct = Math.round(((step + 1) / total) * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
      <div style={{
        flex: 1, height: 4, borderRadius: 99,
        background: 'var(--border2)', overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          borderRadius: 99,
          background: 'linear-gradient(90deg, var(--accent), #5B9BEC)',
          transition: 'width 0.4s ease',
        }} />
      </div>
      <span style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0, fontFamily: 'var(--mono)' }}>
        Step {step + 1} of {total}
      </span>
    </div>
  );
}

function StepWrapper({ visible, children }) {
  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    if (!visible) { setOpacity(0); return; }
    const t = setTimeout(() => setOpacity(1), 20);
    return () => clearTimeout(t);
  }, [visible]);

  if (!visible) return null;
  return (
    <div style={{ opacity, transition: 'opacity 0.15s ease', willChange: 'opacity' }}>
      {children}
    </div>
  );
}

// Step 1 — Company info
function Step1({ companyName, setCompanyName, cageCode, setCageCode }) {
  return (
    <div>
      <div style={{
        width: 52, height: 52, borderRadius: 14,
        background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--accent)', marginBottom: 20,
      }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: '0 0 6px', lineHeight: 1.3 }}>
        Tell us about your organization
      </h2>
      <p style={{ fontSize: 13, color: 'var(--text3)', margin: '0 0 28px', lineHeight: 1.6 }}>
        This information appears on your SSP and compliance reports. You can update it any time in Company Settings.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
            Company / Organization Name <span style={{ color: 'var(--red)' }}>*</span>
          </label>
          <input
            type="text"
            value={companyName}
            onChange={e => setCompanyName(e.target.value)}
            placeholder="Acme Defense Systems LLC"
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '10px 13px', borderRadius: 8,
              border: '1px solid var(--border2)',
              background: 'var(--surface)', color: 'var(--text)',
              fontSize: 13, fontFamily: 'var(--font)',
              outline: 'none', transition: 'border-color 0.15s',
            }}
            onFocus={e => { e.target.style.borderColor = 'var(--accent)'; }}
            onBlur={e => { e.target.style.borderColor = 'var(--border2)'; }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
            CAGE Code <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text3)' }}>(optional)</span>
          </label>
          <input
            type="text"
            value={cageCode}
            onChange={e => setCageCode(e.target.value.toUpperCase())}
            placeholder="1A2B3"
            maxLength={5}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '10px 13px', borderRadius: 8,
              border: '1px solid var(--border2)',
              background: 'var(--surface)', color: 'var(--text)',
              fontSize: 13, fontFamily: 'var(--mono)',
              outline: 'none', transition: 'border-color 0.15s',
            }}
            onFocus={e => { e.target.style.borderColor = 'var(--accent)'; }}
            onBlur={e => { e.target.style.borderColor = 'var(--border2)'; }}
          />
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5 }}>
            5-character identifier assigned by the DoD. Required for CMMC certification.
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 2 — Framework selection
const FRAMEWORK_OPTIONS = [
  {
    id: 'cmmc_l2',
    label: 'CMMC Level 2',
    description: '110 controls · Required for most DoD contractors handling CUI',
    badge: 'Most Common',
    badgeColor: 'var(--accent)',
    badgeBg: 'var(--blue-bg)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
  },
  {
    id: 'nist_171',
    label: 'NIST SP 800-171',
    description: '110 requirements · DFARS compliance for CUI in non-federal systems',
    badge: null,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
      </svg>
    ),
  },
  {
    id: 'other',
    label: 'Other Framework',
    description: 'SOC 2, ISO 27001, HIPAA, NIST CSF, PCI DSS, FedRAMP, and more',
    badge: null,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
  },
];

function Step2({ selectedFramework, setSelectedFramework }) {
  return (
    <div>
      <div style={{
        width: 52, height: 52, borderRadius: 14,
        background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--accent)', marginBottom: 20,
      }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10 9 9 9 8 9"/>
        </svg>
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: '0 0 6px', lineHeight: 1.3 }}>
        Which framework are you targeting?
      </h2>
      <p style={{ fontSize: 13, color: 'var(--text3)', margin: '0 0 24px', lineHeight: 1.6 }}>
        This sets the controls shown in your assessment. You can change frameworks any time.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {FRAMEWORK_OPTIONS.map(opt => {
          const selected = selectedFramework === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => setSelectedFramework(opt.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                border: selected ? '2px solid var(--accent)' : '1.5px solid var(--border2)',
                background: selected ? 'var(--blue-bg)' : 'var(--surface)',
                textAlign: 'left', fontFamily: 'var(--font)',
                transition: 'all 0.15s',
              }}
            >
              <div style={{
                width: 38, height: 38, borderRadius: 9, flexShrink: 0,
                background: selected ? 'rgba(45,125,210,0.15)' : 'var(--bg2)',
                border: `1px solid ${selected ? 'rgba(45,125,210,0.3)' : 'var(--border)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: selected ? 'var(--accent)' : 'var(--text3)',
                transition: 'all 0.15s',
              }}>
                {opt.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: selected ? 'var(--accent)' : 'var(--text)' }}>
                    {opt.label}
                  </span>
                  {opt.badge && (
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 99,
                      background: opt.badgeBg, color: opt.badgeColor,
                    }}>
                      {opt.badge}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.45 }}>
                  {opt.description}
                </div>
              </div>
              <div style={{
                width: 18, height: 18, borderRadius: 99, flexShrink: 0,
                border: selected ? 'none' : '1.5px solid var(--border2)',
                background: selected ? 'var(--accent)' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}>
                {selected && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Step 3 — Start assessment overview
function Step3({ onStartLater }) {
  return (
    <div>
      <div style={{
        width: 52, height: 52, borderRadius: 14,
        background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--accent)', marginBottom: 20,
      }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 11 12 14 22 4"/>
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
        </svg>
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: '0 0 6px', lineHeight: 1.3 }}>
        You're ready to start your assessment
      </h2>
      <p style={{ fontSize: 13, color: 'var(--text3)', margin: '0 0 24px', lineHeight: 1.6 }}>
        Work through each control at your own pace. Your answers are saved automatically — you can stop and resume any time.
      </p>

      <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
        <div style={{
          flex: 1, padding: '16px', borderRadius: 10,
          background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.2)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--accent)', marginBottom: 4 }}>110</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.4 }}>Controls to review</div>
        </div>
        <div style={{
          flex: 1, padding: '16px', borderRadius: 10,
          background: 'var(--green-bg)', border: '1px solid rgba(29,184,122,0.2)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--green)', marginBottom: 4 }}>~45</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.4 }}>Minutes to complete</div>
        </div>
      </div>

      <div style={{
        padding: '14px 16px', borderRadius: 10,
        background: 'var(--surface)', border: '1px solid var(--border)',
        display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8,
      }}>
        <div style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
          Answer each control with <strong style={{ color: 'var(--text)' }}>Yes</strong>, <strong style={{ color: 'var(--text)' }}>Partial</strong>, or <strong style={{ color: 'var(--text)' }}>No</strong>. You'll get a real-time readiness score, gap analysis, and a downloadable SSP when you're done.
        </p>
      </div>

      <button
        onClick={onStartLater}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 12, color: 'var(--text3)', padding: '4px 0',
          fontFamily: 'var(--font)', textDecoration: 'underline',
        }}
      >
        I'll complete the assessment later
      </button>
    </div>
  );
}

// Step 4 — Completion
function Step4() {
  const [animIn, setAnimIn] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimIn(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{ textAlign: 'center', padding: '8px 0' }}>
      <div style={{
        width: 72, height: 72, borderRadius: 99, margin: '0 auto 24px',
        background: 'var(--green-bg)', border: '2px solid rgba(29,184,122,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--green)',
        transform: animIn ? 'scale(1)' : 'scale(0.6)',
        opacity: animIn ? 1 : 0,
        transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease',
      }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: '0 0 10px' }}>
        You're all set!
      </h2>
      <p style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.7, margin: '0 0 8px' }}>
        Your organization profile is saved. Head to the dashboard to start your assessment, upload evidence, and track your compliance readiness score.
      </p>
      <p style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5, margin: 0 }}>
        The onboarding checklist on your dashboard will guide you through the next steps.
      </p>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const TOTAL_STEPS = 4;

export default function OnboardingFlow({ session, profile, setView }) {
  const [show, setShow]                   = useState(false);
  const [step, setStep]                   = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [companyName, setCompanyName]     = useState('');
  const [cageCode, setCageCode]           = useState('');
  const [selectedFramework, setSelectedFramework] = useState('cmmc_l2');
  const [saving, setSaving]               = useState(false);
  const transRef                          = useRef(false);

  const userId = session?.user?.id;

  // Seed inputs from existing profile
  useEffect(() => {
    if (profile?.company_name) setCompanyName(profile.company_name);
    if (profile?.cage_code)    setCageCode(profile.cage_code);
  }, [profile]);

  // Check whether flow has already been completed
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    hasCompletedFlow(userId).then(done => {
      if (!cancelled && !done) setShow(true);
    });
    return () => { cancelled = true; };
  }, [userId]);

  const goToStep = useCallback((next) => {
    if (transRef.current) return;
    transRef.current = true;
    setTransitioning(true);
    setTimeout(() => {
      setStep(next);
      setTransitioning(false);
      transRef.current = false;
    }, 150);
  }, []);

  const markCompleted = useCallback(async () => {
    await markFlowCompleted(userId);
  }, [userId]);

  const handleSkip = useCallback(async () => {
    await markCompleted();
    setShow(false);
  }, [markCompleted]);

  // Step 1 Next: save company_name + cage_code to profiles
  const handleStep1Next = useCallback(async () => {
    if (!companyName.trim()) return;
    setSaving(true);
    try {
      await supabase
        .from('profiles')
        .upsert({ id: userId, company_name: companyName.trim(), cage_code: cageCode.trim(), updated_at: new Date() });
    } catch (_) { /* non-blocking */ }
    setSaving(false);
    goToStep(1);
  }, [userId, companyName, cageCode, goToStep]);

  // Step 2 Next: framework selection (no DB write — framework handled by App.js state)
  const handleStep2Next = useCallback(() => {
    goToStep(2);
  }, [goToStep]);

  // Step 3: Start assessment → mark complete + navigate
  const handleStartAssessment = useCallback(async () => {
    await markCompleted();
    setShow(false);
    if (setView) setView('assessment');
  }, [markCompleted, setView]);

  // Step 3: Skip assessment for now → go to step 4
  const handleStartLater = useCallback(() => {
    goToStep(3);
  }, [goToStep]);

  // Step 4: Go to dashboard
  const handleDone = useCallback(async () => {
    await markCompleted();
    setShow(false);
    if (setView) setView('dashboard');
  }, [markCompleted, setView]);

  if (!show) return null;

  const isStep3 = step === 2;
  const isStep4 = step === 3;

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 9000,
        background: 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
      onClick={e => e.stopPropagation()}
    >
      <div
        style={{
          width: '100%', maxWidth: 560,
          background: 'var(--bg2)',
          borderRadius: 20,
          padding: '36px 40px',
          border: '1px solid var(--border2)',
          boxShadow: '0 32px 80px rgba(0,0,0,0.45)',
          animation: 'ofModalIn 0.3s cubic-bezier(0.34, 1.2, 0.64, 1) forwards',
          position: 'relative',
        }}
      >
        <style>{`
          @keyframes ofModalIn {
            from { opacity: 0; transform: scale(0.95) translateY(8px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
          }
        `}</style>

        {/* Progress */}
        <ProgressBar step={step} total={TOTAL_STEPS} />

        {/* Skip button — only steps 0-2 */}
        {!isStep4 && (
          <button
            onClick={handleSkip}
            style={{
              position: 'absolute', top: 20, right: 20,
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 12, color: 'var(--text3)', padding: '6px 10px',
              borderRadius: 6, fontFamily: 'var(--font)',
              transition: 'color 0.15s, background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.background = 'var(--surface)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.background = 'none'; }}
          >
            Skip for now
          </button>
        )}

        {/* Step content */}
        <div style={{ marginTop: 24, minHeight: 320 }}>
          <StepWrapper visible={step === 0 && !transitioning}>
            <Step1
              companyName={companyName}
              setCompanyName={setCompanyName}
              cageCode={cageCode}
              setCageCode={setCageCode}
            />
          </StepWrapper>
          <StepWrapper visible={step === 1 && !transitioning}>
            <Step2
              selectedFramework={selectedFramework}
              setSelectedFramework={setSelectedFramework}
            />
          </StepWrapper>
          <StepWrapper visible={step === 2 && !transitioning}>
            <Step3 onStartLater={handleStartLater} />
          </StepWrapper>
          <StepWrapper visible={step === 3 && !transitioning}>
            <Step4 />
          </StepWrapper>
        </div>

        {/* Footer actions */}
        <div style={{ marginTop: 28, display: 'flex', justifyContent: isStep4 ? 'center' : 'space-between', alignItems: 'center', gap: 12 }}>
          {!isStep4 && step > 0 && (
            <button
              onClick={() => goToStep(step - 1)}
              disabled={transitioning}
              style={{
                fontSize: 13, fontWeight: 500, padding: '10px 20px',
                borderRadius: 8, cursor: 'pointer',
                border: '1px solid var(--border2)',
                background: 'transparent', color: 'var(--text3)',
                fontFamily: 'var(--font)', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text3)'; }}
            >
              ← Back
            </button>
          )}
          {!isStep4 && step === 0 && <div />}

          {step === 0 && (
            <button
              onClick={handleStep1Next}
              disabled={!companyName.trim() || saving || transitioning}
              style={{
                fontSize: 13, fontWeight: 600, padding: '10px 24px',
                borderRadius: 8, cursor: companyName.trim() && !saving ? 'pointer' : 'not-allowed',
                border: '1px solid var(--accent)',
                background: companyName.trim() && !saving ? 'var(--accent)' : 'var(--border2)',
                color: '#fff', fontFamily: 'var(--font)',
                transition: 'background 0.15s, border-color 0.15s',
                opacity: companyName.trim() && !saving ? 1 : 0.6,
              }}
            >
              {saving ? 'Saving…' : 'Continue →'}
            </button>
          )}
          {step === 1 && (
            <button
              onClick={handleStep2Next}
              disabled={!selectedFramework || transitioning}
              style={{
                fontSize: 13, fontWeight: 600, padding: '10px 24px',
                borderRadius: 8, cursor: 'pointer',
                border: '1px solid var(--accent)',
                background: 'var(--accent)', color: '#fff',
                fontFamily: 'var(--font)', transition: 'background 0.15s',
              }}
            >
              Continue →
            </button>
          )}
          {isStep3 && (
            <button
              onClick={handleStartAssessment}
              style={{
                fontSize: 13, fontWeight: 600, padding: '10px 24px',
                borderRadius: 8, cursor: 'pointer',
                border: '1px solid var(--accent)',
                background: 'var(--accent)', color: '#fff',
                fontFamily: 'var(--font)', transition: 'background 0.15s',
              }}
            >
              Start My Assessment →
            </button>
          )}
          {isStep4 && (
            <button
              onClick={handleDone}
              style={{
                fontSize: 14, fontWeight: 700, padding: '12px 32px',
                borderRadius: 9, cursor: 'pointer',
                border: '1px solid var(--accent)',
                background: 'var(--accent)', color: '#fff',
                fontFamily: 'var(--font)', transition: 'background 0.15s',
                width: '100%', maxWidth: 280,
              }}
            >
              Go to Dashboard →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
